import {styled} from "styled-components";
import PlaySvg from "bootstrap-icons/icons/play-circle-fill.svg?react";
import YoutubeSvg from "bootstrap-icons/icons/youtube.svg?react";
import {useTranslation} from "react-i18next";

/**
 * YoutubeLink — a self-hosted poster image that links to a YouTube video in a new tab.
 *
 * Nothing is fetched from Google until the visitor opens the link, so the page stays free of
 * third-party storage and needs no cookie consent. The poster must be a local asset, never a
 * ytimg.com thumbnail URL, or the page-load request to Google comes back.
 */
export interface YoutubeLinkProps {
  /** The `v` parameter of the YouTube watch URL. */
  videoId: string;
  /** Imported local image, shown at 16:9 with object-fit: cover. */
  poster: string;
  /** Describes the still for screen readers. */
  alt: string;
  className?: string;
}

const Link = styled.a`
  display: block;
  position: relative;
  max-width: 48rem;
  margin-left: auto;
  margin-right: auto;
  border-radius: 0.5rem;
  overflow: hidden;
  color: #fff;

  img {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img,
  &:focus-visible img {
    transform: scale(1.03);
  }
`;

const PlayBadge = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  svg {
    width: 5rem;
    height: 5rem;
    filter: drop-shadow(0 0.25rem 0.75rem rgba(0, 0, 0, 0.6));
    transition: transform 0.3s ease;
  }

  ${Link}:hover &,
  ${Link}:focus-visible & {
    svg {
      transform: scale(1.1);
    }
  }
`;

const Caption = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  font-size: 0.9rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }
`;

export const YoutubeLink = ({videoId, poster, alt, className}: YoutubeLinkProps) => {
  const {t} = useTranslation("landing");
  const href = `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
  return (
    <Link className={className} href={href} rel="noreferrer noopener" target="_blank">
      <img alt={alt} loading="lazy" src={poster} />
      <PlayBadge aria-hidden="true">
        <PlaySvg />
      </PlayBadge>
      <Caption>
        <YoutubeSvg aria-hidden="true" />
        {t("youtube.caption")}
      </Caption>
    </Link>
  );
};
